import React from 'react';
import {
  IFarmInputInvoice,
  IRouteScreenProps,
  ISupplierUserProfile,
  IUser,
} from 'constants/interfaces';
import {apiGetDepotOrders, apiGetUser} from 'constants/Api';
import {
  Box,
  Button,
  ButtonGroup,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {connect} from 'react-redux';
import {Route} from 'react-router-dom';
import NavigationService from 'services/NavigationService';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  orderTableFilters,
  orderTableHeaders,
  orderTableRows,
} from 'screens/Dashboard/Orders/OrderTableSetup';
import {InvoiceState} from 'appredux/reducers/reducer-invoices';
import moment from 'moment';
import {reduxSetInvoices} from 'appredux/actions';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';

import './Orders.scss';

type OrdersProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
  invoiceState: InvoiceState;
};

class Orders extends React.Component<OrdersProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    fetchSize?: number;
    user?: IUser;
    farmerId?: string;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    fetchSize: 20,
    user: undefined,
    farmerId: undefined,
  };

  componentDidMount() {
    const queryParams = new URLSearchParams(this.props.location.search);
    const userId = queryParams.get('userId');
    const noOrders = !this.props.invoiceState.invoices?.length;

    Promise.resolve()
      .then(() => {
        if (userId)
          return apiGetUser(userId).catch((err) => {
            console.error('Failed to fetch user:', err);
          });
      })
      .then((user) => {
        this.setState(
          {
            isLoading: noOrders,
            user,
            farmerId: user?.farmer?.id,
          },
          () => this.loadOrders(),
        );
      });
  }

  loadOrders() {
    const {invoiceState} = this.props;
    const {lastUpdate, invoices} = invoiceState;

    const invoicesAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(30, 'minutes'),
    );
    const noInvoices = !invoices?.length;

    if (invoicesAreExpired || noInvoices || this.state.farmerId)
      this.refreshOrders();
  }

  refreshOrders() {
    const {fetchSize, farmerId} = this.state;

    this.setState({isRefreshing: true}, () => {
      apiGetDepotOrders(fetchSize, farmerId)
        .then((invoices: IFarmInputInvoice[]) => {
          reduxSetInvoices(invoices);
          this.setState({showRefreshedSnackbar: true});
        })
        .catch((err) => {
          alert('Failed to load orders. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false, isRefreshing: false}));
    });
  }

  handleFetchSizeUpdate(size?: number) {
    this.setState({fetchSize: size}, () => {
      this.refreshOrders();
    });
  }

  render() {
    const {invoiceState} = this.props;
    const {lastUpdate, invoices} = invoiceState;
    const {isLoading, isRefreshing, showRefreshedSnackbar, fetchSize, user} =
      this.state;

    const isAdmin = this.props.user.isAdmin;
    const permissionsService = new PermissionsService(this.props.user);
    const canManageOrderStatuses =
      isAdmin &&
      !this.props.user.isCommodityGroup &&
      permissionsService.hasPermission(ESupplierUserPermissions.ORDER_WRITE);
    const canManageOrderPayments =
      isAdmin &&
      !this.props.user.isCommodityGroup &&
      permissionsService.hasPermission(
        ESupplierUserPermissions.ORDER_MANAGE_PAYMENTS,
      );

    const userFullName = user ? `${user.name} ${user.surname}` : '';
    const toolbarActions = (
      <ButtonGroup variant="text">
        {[20, 50, undefined].map((size, index) => (
          <Button
            key={index}
            variant={fetchSize === size ? 'contained' : 'text'}
            disabled={isLoading || isRefreshing}
            disableElevation
            onClick={() => this.handleFetchSizeUpdate(size)}
            size={'small'}
          >
            {size || 'All'}
          </Button>
        ))}
      </ButtonGroup>
    );

    return (
      <>
        <div className="table-screen orders-container">
          <Typography variant="h4" className="page-title">
            Orders{userFullName ? `: ${userFullName}` : ''}
          </Typography>
          <Typography variant="body1" className="page-subtitle">
            Your depot orders
          </Typography>
          <Route
            render={() => (
              <div className="orders-section">
                <OrdersTable
                  isAdmin={isAdmin}
                  canWriteOrders={canManageOrderStatuses}
                  canManageOrderPayments={canManageOrderPayments}
                  invoices={invoices}
                  isLoading={isLoading}
                  isRefreshing={isRefreshing}
                  onRefresh={() => this.refreshOrders()}
                  fetchSize={fetchSize}
                  onRowsPerPageChange={(size) =>
                    this.handleFetchSizeUpdate(size === -1 ? undefined : size)
                  }
                  toolbarActions={toolbarActions}
                />
                <Box mt={1}>
                  <Typography variant={'caption'}>
                    Last updated: {moment(lastUpdate).fromNow()}
                  </Typography>
                </Box>
                <Snackbar
                  anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                  open={showRefreshedSnackbar}
                  onClose={() => this.setState({showRefreshedSnackbar: false})}
                  autoHideDuration={3000}
                  message="Orders refreshed"
                />
              </div>
            )}
          />
        </div>
      </>
    );
  }
}

const OrdersTable = (props: {
  isAdmin: boolean;
  canWriteOrders: boolean;
  canManageOrderPayments: boolean;
  invoices: IFarmInputInvoice[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: VoidFunction;
  fetchSize?: number;
  onRowsPerPageChange?: (size: number) => void;
  toolbarActions?: React.ReactNode;
}) => {
  const {
    invoices,
    isAdmin,
    canWriteOrders,
    canManageOrderPayments,
    isLoading,
    isRefreshing,
    onRefresh,
    fetchSize,
    onRowsPerPageChange,
    toolbarActions,
  } = props;

  const mapRowToInvoice = (row: Row) =>
    invoices.find((invoice) => invoice.id == row.id);

  return (
    <KhulaSupplierTable
      headers={orderTableHeaders(isAdmin)}
      rows={orderTableRows(
        canWriteOrders,
        canManageOrderPayments,
        invoices,
        (invoice) => {
          NavigationService.navigate(`/orders/${invoice.id}`);
        },
      )}
      filtersGroups={orderTableFilters}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      defaultRowsPerPage={typeof fetchSize === 'number' ? fetchSize : -1}
      rowsPerPageOptions={[20, 50, {label: 'All', value: -1}]}
      onRowsPerPageChange={onRowsPerPageChange}
      toolbarActions={toolbarActions}
      onRowSelect={(row) => {
        const invoice = mapRowToInvoice(row);
        if (!invoice) return;
        NavigationService.navigate(`/orders/${invoice.id}`);
      }}
    />
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    invoiceState: state.invoices,
  };
};
export default connect(mapStateToProps)(Orders);
